<template>
	<div v-if="showLanguageBtn" class="multi-language-buttons" :class="{ 'is-selecting': isSelecting }">
		<div
			v-for="lang in availableLanguages"
			:key="lang.key"
			:class="buttonClass"
			@click="() => handleLangChange(lang.key)"
		>
			<!-- PC版本 -->
			<template v-if="mode === 'pc'">
				<SvgIcons :name="lang.icon" class="c-action-svg" />
				<span class="action-block-span">{{ lang.label }}</span>
			</template>

			<!-- 移动端版本 -->
			<template v-else>
				<img :src="lang.image" alt="" />
			</template>
		</div>
	</div>
</template>

<script setup>
import { computed, inject, ref } from 'vue';
import { useLanguageSwitch } from '../hooks/useLanguageSwitch.js';
import i18n, { EN, ZH, ZH_HK } from '@/locales';
import I18_CN_ICON from '@/assets/images/i18-zh-cn.png';
import I18_HK_ICON from '@/assets/images/i18-zh-hk.png';
import I18_EN_ICON from '@/assets/images/i18-en.png';

const props = defineProps({
	selectHandler: {
		type: Function,
		default: null
	}
});

const mode = inject('mode');
const buttonClass = inject('normalButtonClass');

const { languageMap, showLanguageBtn, changeLocaleLang } = useLanguageSwitch();
const isSelecting = ref(false);

// 语言图标映射
const langIconMap = {
	[ZH]: I18_CN_ICON,
	[ZH_HK]: I18_HK_ICON,
	[EN]: I18_EN_ICON
};
const sortRule = [ZH, EN, ZH_HK];

// 可用语言列表（除当前语言外的其他语言）
const availableLanguages = computed(() => {
	return Object.keys(languageMap)
		.sort((a, b) => sortRule.indexOf(a) - sortRule.indexOf(b))
		.filter(key => key !== i18n.locale)
		.map(key => ({
			key,
			icon: languageMap[key].icon,
			label: languageMap[key].label,
			image: langIconMap[key]
		}));
});

// 语言切换处理
const handleLangChange = async lang => {
	if (isSelecting.value) return;

	isSelecting.value = true;
	try {
		// 定制场景可接管切换动作，返回 true 表示已完成处理
		if (props.selectHandler) {
			try {
				const handled = await props.selectHandler(lang);
				if (handled) return;
			} catch (error) {
				// 定制处理器异常时回退默认切换，避免按钮无响应
				console.error('[MultiLanguageButton] selectHandler 执行失败，回退默认语言切换', error);
			}
		}
		changeLocaleLang(lang);
	} finally {
		isSelecting.value = false;
	}
};
</script>

<style lang="less" scoped>
.multi-language-buttons {
	display: flex;
	flex-direction: column;

	&.is-selecting {
		pointer-events: none;
		opacity: 0.6;
	}
}
</style>
